import React, { useState, useEffect } from 'react';
import { Box, Paper, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, Typography, Alert, Chip, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import api from '../services/api';

interface StockItem {
  variant_id: string;
  variant_name: string;
  variant_sku: string;
  product_name: string;
  product_item_code: string;
  category_name: string;
  current_stock: number;
  reserved_stock: number;
  available_stock: number;
  minimum_stock: number;
  unit: string;
  image: string;
  new_quantity?: number;
}

interface Warehouse {
  id: string;
  name: string;
  code: string;
}

export const BulkStockAdjustmentPage: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState('Bulk stock adjustment');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      loadStockData();
    }
  }, [selectedWarehouse, search, page]);

  const loadWarehouses = async () => {
    try {
      const response = await api.get('/admin/inventory/warehouses');
      setWarehouses(response.data);
      if (response.data.length > 0) {
        setSelectedWarehouse(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadStockData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/inventory/bulk-stock-view', {
        params: { warehouse_id: selectedWarehouse, search, page, page_size: 100 }
      });
      setStockItems(response.data.items.map((item: StockItem) => ({ ...item, new_quantity: item.current_stock })));
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to load stock data:', error);
      setMessage({ type: 'error', text: 'Failed to load stock data' });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (variantId: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setStockItems(prev => prev.map(item => item.variant_id === variantId ? { ...item, new_quantity: numValue } : item));
    }
  };

  const handleSave = async () => {
    const adjustments = stockItems.filter(item => item.new_quantity !== item.current_stock).map(item => ({ variant_id: item.variant_id, adjustment_type: 'set', quantity: item.new_quantity || 0 }));

    if (adjustments.length === 0) {
      setMessage({ type: 'error', text: 'No changes to save' });
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/admin/inventory/bulk-stock-adjust', { adjustments, warehouse_id: selectedWarehouse, reason });
      setMessage({ type: 'success', text: `Successfully updated ${response.data.processed} items` });
      loadStockData();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save adjustments' });
    } finally {
      setSaving(false);
    }
  };

  const getStockStatus = (current: number, minimum: number) => {
    if (current === 0) return <Chip label="Out of Stock" color="error" size="small" />;
    if (current < minimum) return <Chip label="Low Stock" color="warning" size="small" />;
    return <Chip label="In Stock" color="success" size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Bulk Stock Adjustment</Typography>
      
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
          <TextField select label="Warehouse" value={selectedWarehouse} onChange={(e) => setSelectedWarehouse(e.target.value)} sx={{ minWidth: 200 }} size="small">{warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}</TextField>
          <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Product name, SKU..." size="small" sx={{ flexGrow: 1 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>, endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon /></IconButton></InputAdornment> }} />
          <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} size="small" sx={{ minWidth: 250 }} />
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || loading}>{saving ? 'Saving...' : 'Save All'}</Button>
          <Tooltip title="Refresh"><IconButton onClick={loadStockData} disabled={loading}><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>SKU</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Current</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Reserved</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Available</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Min</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 120 }}>New Quantity</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center">Loading...</TableCell></TableRow>
            ) : stockItems.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center">No products found</TableCell></TableRow>
            ) : (
              stockItems.map((item) => {
                const change = (item.new_quantity || 0) - item.current_stock;
                return (
                  <TableRow key={item.variant_id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.product_name}</Typography><Typography variant="caption" color="text.secondary">{item.variant_name}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{item.variant_sku}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{item.category_name}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2">{item.current_stock}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color="warning.main">{item.reserved_stock}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.available_stock}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color="text.secondary">{item.minimum_stock}</Typography></TableCell>
                    <TableCell>{getStockStatus(item.current_stock, item.minimum_stock)}</TableCell>
                    <TableCell align="right"><TextField type="number" value={item.new_quantity || 0} onChange={(e) => handleQuantityChange(item.variant_id, e.target.value)} size="small" sx={{ width: 100 }} inputProps={{ min: 0, style: { textAlign: 'right' } }} /></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 500, color: change > 0 ? 'success.main' : change < 0 ? 'error.main' : 'text.secondary' }}>{change > 0 ? `+${change}` : change}</Typography></TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > 100 && <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}><Button size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button><Typography sx={{ px: 2, py: 1 }}>Page {page} of {Math.ceil(total / 100)}</Typography><Button size="small" disabled={page >= Math.ceil(total / 100)} onClick={() => setPage(p => p + 1)}>Next</Button></Box>}
    </Box>
  );
};

export default BulkStockAdjustmentPage;
